import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import services from "../views/Orders/services";
import { orderCardTheme } from "../views/Orders/components/orderCardTheme";
import { useDeliveryOptionsLauncher } from "../components/DeliveryOptions/useDeliveryOptionsLauncher";
import FinalEstimateReviewBar from "../components/FinalEstimateReviewBar";
import { useUserDetailsContext } from "./userDetailsContext";
import {
  enqueueFinalEstimateReview,
  type FinalEstimateReview,
} from "./finalEstimateReviewQueue";

type FinalEstimateReviewContextValue = {
  /** Queues the review, soonest-expiring first; it opens once nothing else is on screen. */
  openReview: (review: FinalEstimateReview) => void;
  /** Queues the OH's pending reviews from the backend. */
  loadPendingReviews: () => Promise<void>;
};

const FinalEstimateReviewContext = createContext<
  FinalEstimateReviewContextValue | undefined
>(undefined);

/** Returns null outside the provider instead of throwing. */
export function useFinalEstimateReviewOptional(): FinalEstimateReviewContextValue | null {
  return useContext(FinalEstimateReviewContext) ?? null;
}

type Props = {
  children: React.ReactNode;
};

/**
 * Final estimate review for the OH, shown like the ETA alert: the backend
 * builds the final estimate (isReviewed false) while the enquiry stays
 * FINAL_ESTIMATE_REQUESTED and notifies the OH; this opens the estimate modal
 * on it (the same edits as Generate Estimate; it can't be closed). MARK
 * REVIEWED saves, marks it reviewed and shares it → FINAL_ESTIMATE_SHARED.
 * VIEW ENQUIRY closes it and opens the enquiry with a bottom bar (like the
 * ETA alert reminder) whose REVIEW ESTIMATE opens it again; the next review
 * waits meanwhile. On expiry it closes for good. An ETA Hard lock on another enquiry
 * does not block it. Reviews come from the SuprSend feed and, on load, from the
 * pending list.
 */
export const FinalEstimateReviewProvider: React.FC<Props> = ({ children }) => {
  const userDetails = useUserDetailsContext();
  const navigate = useNavigate();
  const { launchReviewModal, deliveryOptionsModal } =
    useDeliveryOptionsLauncher();
  const [queue, setQueue] = useState<FinalEstimateReview[]>([]);
  const [active, setActive] = useState<FinalEstimateReview | null>(null);
  // VIEW ENQUIRY: the active review waits behind the bottom bar.
  const [isParked, setIsParked] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const toast = useToast();

  const openReview = useCallback(
    (review: FinalEstimateReview) => {
      setQueue((current) =>
        enqueueFinalEstimateReview(current, active?.estimateNo, review),
      );
    },
    [active?.estimateNo],
  );

  const finishActive = useCallback(() => {
    setActive(null);
    setIsParked(false);
  }, []);

  const openActive = useCallback(
    (review: FinalEstimateReview) =>
      launchReviewModal({
        ...review,
        onReviewed: finishActive,
        onDismissed: finishActive,
        onViewEnquiry: () => {
          setIsParked(true);
          navigate(`/orders/${review.orderNo}`);
        },
      }).then((result) => {
        if (result !== "opened") {
          finishActive();
        }
        return result;
      }),
    [launchReviewModal, finishActive, navigate],
  );

  useEffect(() => {
    if (active || queue.length === 0) {
      return;
    }
    const [next, ...rest] = queue;
    setQueue(rest);
    setActive(next);
    void openActive(next);
  }, [active, queue, openActive]);

  const handleReviewEstimate = useCallback(async () => {
    if (!active) {
      return;
    }
    setIsReopening(true);
    try {
      const result = await openActive(active);
      if (result === "opened") {
        setIsParked(false);
      } else if (result === "expired") {
        toast({
          title: "Final estimate expired",
          description: `The final estimate of enquiry #${active.orderNo} has expired and can no longer be reviewed.`,
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } finally {
      setIsReopening(false);
    }
  }, [active, openActive, toast]);

  const loadPendingReviews = useCallback(async () => {
    if (!userDetails?._id || !localStorage.getItem("auth_orca")) {
      return;
    }
    try {
      const response = await services.getPendingEstimateReviews();
      for (const review of response?.reviews ?? []) {
        if (!review?.orderId || !review.estimateNo) {
          continue;
        }
        openReview({
          orderId: review.orderId,
          orderNo: review.orderNo,
          estimateNo: review.estimateNo,
          expiryTime: review.expiryTime,
        });
      }
    } catch (error) {
      console.error("Failed to fetch pending final estimate reviews", error);
    }
  }, [userDetails?._id, openReview]);

  useEffect(() => {
    void loadPendingReviews();
    // Load once per user; loadPendingReviews also changes with the active review.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails?._id]);

  const contextValue = useMemo(
    () => ({ openReview, loadPendingReviews }),
    [openReview, loadPendingReviews],
  );

  return (
    <FinalEstimateReviewContext.Provider value={contextValue}>
      {children}
      {/* Same theme as supplier discovery, where this modal normally opens */}
      {active && (
        <ThemeProvider theme={orderCardTheme}>{deliveryOptionsModal}</ThemeProvider>
      )}
      {active && isParked && (
        <FinalEstimateReviewBar
          orderNo={active.orderNo}
          onReviewEstimate={handleReviewEstimate}
          isLoading={isReopening}
        />
      )}
    </FinalEstimateReviewContext.Provider>
  );
};

export default FinalEstimateReviewProvider;
